import { apiCallback } from './index';
import { ApiCallArgs, GraphApiDefinition, RestApiDefinition } from './types';

/**
 * Extracts the argument name from the URL. It should have the format `${name}`
 */
type PartToPropName<Part extends string> = Part extends `\${${infer Name}}` ? Name : never;

type RemoveLeadingSeparator<Path extends string> = Path extends `/${infer Rest}` ? Rest : Path;

type PathTemplateToPropNames<Path extends string> =
  RemoveLeadingSeparator<Path> extends `${infer Part}/${infer Rest}`
    ? PartToPropName<Part> | PathTemplateToPropNames<Rest>
    : PartToPropName<Path>;

/**
 * Extracts object type from URL template.
 *
 * @example
 *
 * type Parameters = PathTemplateToParams<'failed-order-payments/${customerOrderId}/payment-cards/${paymentCardId}'> // => { customerOrderId: string; paymentCardId: string; }
 *
 */
type PathTemplateToParams<Path extends string> = {
  [P in PathTemplateToPropNames<Path>]: string;
};

/**
 * Typed variation of {@link apiCallback}. This function will return a new generic function, which parameters can be applied before exporting, such as the response, the request body, etc. See the example for how to use it.
 *
 * @example
 *
 * <caption>In this example, the URL parameters will be inferred from the `endpoint`</caption>
 *
 * const patch = {
 *   defaultVersion: 1,
 *   endpoint: 'failed-order-payments/${customerOrderId}/payment-cards/${paymentCardId}',
 *   service: 'order-payment',
 *   verbs: ['patch'],
 *   timeout: TIMEOUT_CONFIG,
 * } as const satisfies RestApiDefinition; // `as const` is required so the URL parameters can be inferred
 *
 * interface PatchFailedOrderPaymentBody { // request body
 * }
 *
 * interface PlaceFailedOrderPaymentResponse { // response
 * }
 *
 * export default {
 *   patch: restApiCallback(patch, 'patch')<
 *     PlaceFailedOrderPaymentResponse,
 *     PatchFailedOrderPaymentBody
 *   >, // The generic types after the `restApiCallback` apply to the function returned by `apiCallback`
 * }
 *
 * @example
 *
 * <caption>The definition can be inlined. This allows us to omit the `as const`</caption>
 *
 * interface PatchFailedOrderPaymentBody { // request body
 * }
 *
 * interface PlaceFailedOrderPaymentResponse { // response
 * }
 *
 * export default {
 *   patch: restApiCallback(
 *     {
 *       defaultVersion: 1,
 *       endpoint: 'failed-order-payments/${customerOrderId}/payment-cards/${paymentCardId}',
 *       service: 'order-payment',
 *       verbs: ['patch'],
 *       timeout: TIMEOUT_CONFIG,
 *     },
 *     'patch',
 *   )<PlaceFailedOrderPaymentResponse, PatchFailedOrderPaymentBody>,
 * } // The generic types after the `restApiCallback` apply to the function returned by `apiCallback`
 *
 */
export function restApiCallback<
  const TApi extends RestApiDefinition = RestApiDefinition,
  TArgs extends object = PathTemplateToParams<TApi['endpoint']>,
>(
  api: TApi,
  verb: TApi['verbs'][number], // This will only allow one of the values passed to `api.verbs`
): <TResponse = unknown, TBody = never, THeaders extends Record<string, string> = never>(
  args: TArgs & ApiCallArgs<TBody, THeaders>,
) => WtrApiCall<TResponse> {
  return apiCallback(api, verb);
}

export function graphApiCallback<
  VariablesInput = object,
  VariablesOutput = object,
  QueryDefinition = string,
  const TApi extends GraphApiDefinition<
    QueryDefinition,
    VariablesInput,
    VariablesOutput
  > = GraphApiDefinition<QueryDefinition, VariablesInput, VariablesOutput>,
>(api: TApi): <TResponse = unknown>(args: ApiCallArgs) => WtrApiCall<TResponse> {
  return apiCallback(api, 'post');
}
